// AddUser.tsx

import React, {useEffect, useState} from 'react';
import {t} from 'i18next';
import {Grid, Checkbox, Typography, Box} from '@mui/material';
import {ArrowDropUp, InfoOutline} from '@mui/icons-material';
import ArrButton from '../../common/arr-button/ArrButton';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {ReactComponent as InfoIcon} from '../../../../src/assets/images/info-icon.svg';
import {ReactComponent as CheckedIcon} from '../../../../src/assets/images/icon-checked.svg';
import {ReactComponent as UnCheckedIcon} from '../../../../src/assets/images/icon-unchecked.svg';
import {ISelectItem} from '../../../shared/types';
import {
  FIRST_LAST_NAME_REGEX,
  rolesList,
  TOAST_DURATION,
} from '../../../shared/constants';
import './AddUser.scss';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrMultiSelectDropdown from '../../common/arr-select-dropdown/ArrMultiSelectDropdown';
import ArrToast from '../../common/arr-toast/ArrToast';
import {ReactComponent as CloseToast} from '../../../../src/assets/images/close-toast.svg';
import {
  useAddUser,
  useSecurityGroups,
  useUserMeta,
} from '../../../hooks/users/useUsers';
import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import {isEmpty} from 'lodash';
import {IAddUserPayload, SecurityGroupItem} from '../../../api/types/user.type';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';

type AddUserProps = {
  showDrawer: boolean;
  handleDrawer: (value: React.SetStateAction<boolean>) => void;
  onUserAdded?: () => void;
};
const fullWidthGridStyle = {xs: 12, sm: 12};

export const AddUser: React.FC<AddUserProps> = ({
  showDrawer,
  handleDrawer,
  onUserAdded,
}) => {
  const prefill = useSelector(
    (state: RootState) => state.roleImpersonation.prefill
  );
  // Server field-level errors
  const [fieldErrors, setFieldErrors] = useState<{
    FirstName?: string;
    LastName?: string;
    PrimaryEmail?: string;
  }>({});
  const [fName, setFName] = useState<string>('');
  const [isFNameValid, setIsFNameValid] = useState<boolean>(true);
  const [isFNameMaxLengthValid, setIsFNameMaxLengthValid] =
    useState<boolean>(true);
  const [lName, setLName] = useState<string>('');
  const [isLNameValid, setIsLNameValid] = useState<boolean>(true);
  const [isLNameMaxLengthValid, setIsLNameMaxLengthValid] =
    useState<boolean>(true);
  const MAX_NAME_LENGTH = 50;
  const [primaryEmail, setPrimaryEmail] = useState<string>('');
  const [isPrimaryEmailValid, setIsPrimaryEmailValid] = useState<boolean>(true);
  const [isOpenRoleDef, setIsOpenRoleDef] = useState<boolean>(false);
  const [role, setRole] = useState<ISelectItem | null>(null);
  const [domain, setDomain] = useState<ISelectItem | null>(null);
  const [project, setProject] = useState<ISelectItem | null>(null);
  const [isBillableUser, setIsBillableUser] = useState<boolean>(false);
  const [isAttorneyProfile, setIsAttorneyProfile] = useState<boolean>(false);
  const [securityGroup, setSecurityGroup] = useState<SecurityGroupItem[]>([]);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [touchedFields, setTouchedFields] = useState<{
    fName: boolean;
    lName: boolean;
    primaryEmail: boolean;
  }>({
    fName: false,
    lName: false,
    primaryEmail: false,
  });

  const {data: userMeta} = useUserMeta();
  const domainList =
    userMeta?.roleId == -2 && userMeta?.domainList
      ? Object.entries(userMeta.domainList).map(([key, value]) => ({
          key: Number(key),
          value,
        }))
      : [];
  const rolesOptions = userMeta?.roles ?? [];
  const isLoggedInUserBillable = userMeta?.isBillable ?? false;
  const domainOptions =
    userMeta?.roleId === -2
      ? (domainList ?? [])
      : (userMeta?.domainListSelect ?? []);
  const projectOptions = userMeta?.projectListSelect ?? [];
  const roleId = role?.key;
  useEffect(() => {
    if (userMeta?.roleId === 1 || userMeta?.roleId === 2) {
      setProject(
        prefill?.project
          ? {
              key: String(prefill.project.id),
              value: String(prefill.project.name ?? ''),
            }
          : {}
      );
    } else {
      setProject(null);
    }
  }, [roleId]);

  const projectId = project?.key;
  const roleIdType = typeof roleId === 'number';
  const numericProjectId = Number(projectId);
  const {data: securityGRPsOptions = []} = useSecurityGroups(
    Number(roleId),
    numericProjectId,
    roleIdType &&
      roleId > 1 &&
      Number.isFinite(numericProjectId) &&
      numericProjectId > 0
  );

  const getRequiredFields = () => {
    const baseFields = {
      fName,
      lName,
      primaryEmail,
      role,
    };
    const roleId = role?.key;
    if (roleId == -2) {
      return {
        ...baseFields,
        domain,
      };
    } else if (roleId === 1) {
      return {
        ...baseFields,
        project,
      };
    } else if (roleId === 2 || roleId === 3) {
      return {
        ...baseFields,
        project,
        securityGroup,
      };
    }
    return baseFields;
  };
  const isFormValid =
    Object.values(getRequiredFields()).every(
      (value) => value?.toString().trim() !== '' && !isEmpty(value)
    ) &&
    isFNameValid &&
    isLNameValid &&
    isPrimaryEmailValid &&
    isFNameMaxLengthValid &&
    isLNameMaxLengthValid;

  const handleBlur = (fieldName: keyof typeof touchedFields) => {
    setTouchedFields((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
  };

  const handleTextInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const {value, name} = event.target;

    if (name === 'fName') {
      setFName(value);
      if (value) {
        const isValid = FIRST_LAST_NAME_REGEX.test(value);
        setIsFNameValid(isValid);
        setIsFNameMaxLengthValid(value.length <= MAX_NAME_LENGTH);
      } else {
        setIsFNameValid(false);
        setIsFNameMaxLengthValid(true);
      }
    } else if (name === 'lName') {
      setLName(value);
      if (value === '') {
        setIsLNameValid(false);
        setIsLNameMaxLengthValid(true);
      } else {
        const isValid = FIRST_LAST_NAME_REGEX.test(value);
        setIsLNameValid(isValid);
        setIsLNameMaxLengthValid(value.length <= MAX_NAME_LENGTH);
      }
    } else {
      setPrimaryEmail(value);
      const trimmed = value.trim();
      if (trimmed === '') {
        setIsPrimaryEmailValid(false);
      } else {
        const emailRegex =
          /^[A-Za-z0-9_%+-]+(?:\.[A-Za-z0-9_%+-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;
        const isValid = emailRegex.test(trimmed); // && EMAIL_ADDRESS_REGEX.test(trimmed);
        setIsPrimaryEmailValid(isValid);
      }
    }
  };

  useEffect(() => {
    setSecurityGroup([]);
  }, [project]);

  useEffect(() => {
    const roleId = role?.key;
    if (roleId == -2) {
      setProject(null);
      setSecurityGroup([]);
      setIsAttorneyProfile(false);
    } else if (roleId === 1) {
      setDomain(null);
      setSecurityGroup([]);
      setIsAttorneyProfile(false);
    } else if (roleId === 2) {
      setDomain(null);
    } else if (roleId === 3) {
      setDomain(null);
      setIsAttorneyProfile(false);
    } else if (roleId === -1) {
      setDomain(null);
      setProject(null);
      setSecurityGroup([]);
      setIsAttorneyProfile(false);
      setIsBillableUser(false);
    }
  }, [role]);

  const toggleRoleDef = () => {
    setIsOpenRoleDef((prev) => !prev);
  };
  const getSecurityGrpsIds = () => {
    if (isEmpty(securityGroup)) return [];
    return securityGroup.map((i) => i.key.toString());
  };

  const {
    data: addUserData,
    mutate: saveUser,
    error: addUserError,
    isPending: isLoading,
  } = useAddUser();

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: IAddUserPayload = {
      firstName: fName.trim(),
      lastName: lName.trim(),
      emailId: primaryEmail.trim(),
      localeCode: 'en-us',
      roleId: roleId?.toString() || '',
      entityId: Number(domain?.key) || 0,
      projectId: Number(projectId) || 0,
      securityGroupId: getSecurityGrpsIds(),
      isBillable: isLoggedInUserBillable || isBillableUser,
      isActive: true,
      ssoEnabled: true,
      recoveryEmailId: '',
      isAttorneyProfile: isAttorneyProfile,
    };

    saveUser(payload, {
      onSuccess: () => {
        // Clear form but keep drawer open to show success toast
        handleClear();
        setTouchedFields({
          fName: false,
          lName: false,
          primaryEmail: false,
        });
        setFieldErrors({});
        setShowToast(true);

        // Trigger grid refresh if callback provided
        if (onUserAdded) {
          onUserAdded();
        }
      },
      onError: (error: any) => {
        // Try to extract field errors from server response
        if (error?.response?.data?.errors) {
          setFieldErrors(error.response.data.errors);
        } else {
          setFieldErrors({});
          setShowToast(true);
        }
      },
    });
  };
  const handleClear = () => {
    setFName('');
    setLName('');
    setPrimaryEmail('');
    setIsOpenRoleDef(false);
    setRole(null);
    setDomain(null);
    setProject(null);
    setIsBillableUser(false);
    setIsAttorneyProfile(false);
    setSecurityGroup([]);
  };

  const contentBody = (
    <Box className="add-user-container">
      <Box className="add-user-header-box">
        <Box className="title-box">
          <Typography
            className=" typo-text headline3_size title"
            data-testid="add-user-title"
          >
            {t('userManagement.addUser.title')}&nbsp;
          </Typography>
          <ArrTooltipWrapper
            trigger={
              <InfoIcon
                fontSize="medium"
                className="info-icon"
                data-testid="add-user-title-info"
              />
            }
          >
            {t('userManagement.addUser.titleInfo')}
          </ArrTooltipWrapper>
        </Box>
        <Box className="action-buttons-box">
          <ArrButton
            variant="secondary"
            size="medium"
            onClick={() => {
              handleDrawer(false);
            }}
            disabled={false}
            dataTestId="add-user-cancel-button"
          >
            {t('common.cancel')}
          </ArrButton>
          <ArrButton
            variant="primary"
            size="medium"
            type="submit"
            disabled={!isFormValid}
            dataTestId="add-user-save"
            form="add-user-form"
          >
            {t('common.save')}
          </ArrButton>
        </Box>
      </Box>
      <Box className="content-box">
        <form
          aria-labelledby="add-user-title"
          onSubmit={handleSave}
          id="add-user-form"
          data-testid={`add-user-title-form`}
        >
          <Grid container columnSpacing={4} rowSpacing={3}>
            <Grid size={{xs: 12, sm: 6}}>
              <ArrTextField
                id="first-name-input"
                state={
                  !isFNameValid ||
                  fieldErrors.FirstName ||
                  !isFNameMaxLengthValid ||
                  (touchedFields.fName && !fName)
                    ? 'error'
                    : 'enabled'
                }
                label={t('userManagement.addUser.fName')}
                required
                name="fName"
                aria-label={t('userManagement.addUser.fName')}
                value={fName}
                onChange={handleTextInputChange}
                onBlur={() => handleBlur('fName')}
                dataTestId="first-name-input"
                supportText={(() => {
                  if (fieldErrors.FirstName) {
                    return fieldErrors.FirstName;
                  }
                  if (touchedFields.fName && !fName) {
                    return t('userManagement.addUser.fnameRequired');
                  }
                  if (!isFNameValid && fName) {
                    return t('userManagement.addUser.nameValidationMsg');
                  }
                  if (!isFNameMaxLengthValid) {
                    return t('userManagement.addUser.maxLengthMsg', {
                      max: MAX_NAME_LENGTH,
                    });
                  }
                  return '';
                })()}
                showErrorIcon={
                  !!fieldErrors.FirstName ||
                  !isFNameValid ||
                  !isFNameMaxLengthValid ||
                  (touchedFields.fName && !fName)
                }
                aria-invalid={
                  !!fieldErrors.FirstName ||
                  !isFNameValid ||
                  !isFNameMaxLengthValid ||
                  (touchedFields.fName && !fName)
                }
              />
            </Grid>
            <Grid size={{xs: 12, sm: 6}}>
              <ArrTextField
                id="last-name-input"
                state={
                  !!fieldErrors.LastName ||
                  !isLNameValid ||
                  !isLNameMaxLengthValid ||
                  (touchedFields.lName && !lName)
                    ? 'error'
                    : 'enabled'
                }
                label={t('userManagement.addUser.lName')}
                required
                name="lName"
                aria-label={t('userManagement.addUser.lName')}
                value={lName}
                onChange={handleTextInputChange}
                onBlur={() => handleBlur('lName')}
                dataTestId="last-name-input"
                supportText={(() => {
                  if (fieldErrors.LastName) {
                    return fieldErrors.LastName;
                  }
                  if (touchedFields.lName && !lName) {
                    return t('userManagement.addUser.lnameRequired');
                  }
                  if (!isLNameValid && lName) {
                    return t('userManagement.addUser.nameValidationMsg');
                  }
                  if (!isLNameMaxLengthValid) {
                    return t('userManagement.addUser.maxLengthMsg', {
                      max: MAX_NAME_LENGTH,
                    });
                  }
                  return '';
                })()}
                showErrorIcon={
                  !!fieldErrors.LastName ||
                  !isLNameValid ||
                  !isLNameMaxLengthValid ||
                  (touchedFields.lName && !lName)
                }
                aria-invalid={
                  !!fieldErrors.LastName ||
                  !isLNameValid ||
                  !isLNameMaxLengthValid ||
                  (touchedFields.lName && !lName)
                }
              />
            </Grid>
            <Grid size={fullWidthGridStyle}>
              <ArrTextField
                id="email-input"
                state={
                  !!fieldErrors.PrimaryEmail ||
                  !isPrimaryEmailValid ||
                  (touchedFields.primaryEmail && !primaryEmail.trim())
                    ? 'error'
                    : 'enabled'
                }
                label={t('userManagement.addUser.primaryEmail')}
                required
                aria-label={t('userManagement.addUser.primaryEmail')}
                value={primaryEmail}
                onChange={handleTextInputChange}
                onBlur={() => handleBlur('primaryEmail')}
                dataTestId="email-input"
                supportText={(() => {
                  if (fieldErrors.PrimaryEmail) {
                    return fieldErrors.PrimaryEmail;
                  }
                  if (touchedFields.primaryEmail && !primaryEmail.trim()) {
                    return t('userManagement.addUser.emailRequired');
                  }
                  if (!isPrimaryEmailValid && primaryEmail) {
                    return t(
                      'userManagement.addUser.primaryEmailValidationMsg'
                    );
                  }
                  return '';
                })()}
                name="primaryEmail"
                showErrorIcon={
                  !!fieldErrors.PrimaryEmail ||
                  !isPrimaryEmailValid ||
                  (touchedFields.primaryEmail && !primaryEmail.trim())
                }
                aria-invalid={
                  !!fieldErrors.PrimaryEmail ||
                  !isPrimaryEmailValid ||
                  (touchedFields.primaryEmail && !primaryEmail.trim())
                }
              />
            </Grid>

            {roleId && roleId != -1 && !isLoggedInUserBillable && (
              <Grid size={fullWidthGridStyle}>
                <Box className="check-box-input">
                  <Checkbox
                    data-testid="add-user-billable-checkbox"
                    className="add-user-checkbox"
                    checked={isBillableUser}
                    size="small"
                    icon={<UnCheckedIcon />}
                    checkedIcon={<CheckedIcon />}
                    aria-labelledby="checkbox-label-billableUser"
                    onChange={(e) => setIsBillableUser(e.target.checked)}
                    id="billable-checkbox"
                    inputProps={{'data-testid': 'billable-checkbox'}}
                  />
                  <Box className="checkbox-label">
                    <Typography
                      className="typo-text body3_size label"
                      id="checkbox-label-billable-user"
                      data-testid="checkbox-label-billable-user"
                    >
                      {t('userManagement.addUser.billableUser')}&nbsp;
                    </Typography>
                    <ArrTooltipWrapper
                      trigger={
                        <InfoOutline
                          fontSize="small"
                          className="info-icon icon-size"
                          data-testid="info-icon-billable-user"
                        />
                      }
                    >
                      {t('userManagement.addUser.billableUserInfo')}
                    </ArrTooltipWrapper>
                  </Box>
                </Box>
              </Grid>
            )}
            <Grid size={fullWidthGridStyle}>
              <ArrSingleSelectDropdown
                dropdownLabel={t('userManagement.addUser.selectRole')}
                dropdownItems={rolesOptions}
                selectedValues={role}
                dropdownItemKey={'key'}
                dropdownItemDisplay={'value'}
                formControlRestProps={{required: true}}
                onSelectionChange={setRole}
                dataTestId="asd-add-user-select-role"
              />
            </Grid>
            <Grid size={fullWidthGridStyle}>
              <Box className="role-def-box">
                <ArrButton
                  variant="text"
                  size="xs"
                  onClick={toggleRoleDef}
                  startIcon={<InfoOutline className="info-icon icon-size" />}
                  endIcon={
                    isOpenRoleDef ? (
                      <ArrowDropUp data-testid="arrow-drop-up" />
                    ) : (
                      <ArrowDropDownIcon data-testid="arrow-drop-down" />
                    )
                  }
                  dataTestId="role-ref-btn"
                >
                  {t('userManagement.addUser.roleDef.title')}
                </ArrButton>
                {isOpenRoleDef && (
                  <Box className="content" data-testid="role-def-content">
                    {rolesList.map((i) => (
                      <Box key={i}>
                        <Typography
                          component="span"
                          className="role-name"
                          data-testid={`role-name-${i}`}
                        >
                          {t('common.roleName.' + i) + ': '}
                        </Typography>
                        <Typography
                          component="span"
                          className="typo-text body5_size role-desc"
                          data-testid={`role-desc-${i}`}
                        >
                          {t(`userManagement.addUser.roleDef.${i}`)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>
            {roleId == 2 && (
              <Grid size={fullWidthGridStyle}>
                <Box className="check-box-input">
                  <Checkbox
                    data-testid="add-user-attorney-profile-checkbox"
                    className="add-user-checkbox"
                    checked={isAttorneyProfile}
                    size="small"
                    icon={<UnCheckedIcon />}
                    checkedIcon={<CheckedIcon />}
                    aria-labelledby="checkbox-label-attorneyProfile"
                    onChange={(e) => setIsAttorneyProfile(e.target.checked)}
                    inputProps={{'data-testid': 'attorney-profile-checkbox'}}
                  />
                  <Box
                    className="checkbox-label"
                    id="checkbox-label-attorney-profile"
                    data-testid="checkbox-label-attorney-profile"
                  >
                    <Typography
                      className="typo-text body3_size label"
                      data-testid={`add-user-attorney-profile`}
                    >
                      {t('userManagement.addUser.attorneyProfile')}&nbsp;
                    </Typography>
                    <ArrTooltipWrapper
                      trigger={
                        <InfoOutline
                          fontSize="small"
                          className="info-icon icon-size"
                          data-testid="info-icon-attorney-profile"
                        />
                      }
                    >
                      {t('userManagement.addUser.attorneyProfileInfo')}
                    </ArrTooltipWrapper>
                  </Box>
                </Box>
              </Grid>
            )}
            {roleId == -2 && (
              <Grid size={fullWidthGridStyle}>
                <ArrSingleSelectDropdown
                  dropdownLabel={t('userManagement.addUser.selectDomain')}
                  dropdownItems={domainOptions}
                  selectedValues={domain}
                  onSelectionChange={setDomain}
                  dropdownItemKey={'key'}
                  dropdownItemDisplay={'value'}
                  formControlRestProps={{required: true}}
                  dataTestId="asd-add-user-select-domain"
                  searchable
                  showNoResultsText
                />
              </Grid>
            )}
            {roleIdType && [1, 2, 3].includes(roleId) && (
              <Grid size={fullWidthGridStyle}>
                <ArrSingleSelectDropdown
                  dropdownLabel={t('userManagement.addUser.selectProject')}
                  dropdownItems={projectOptions}
                  selectedValues={project}
                  onSelectionChange={setProject}
                  dropdownItemKey={'key'}
                  dropdownItemDisplay={'value'}
                  formControlRestProps={{required: true}}
                  dataTestId="asd-add-user-select-project"
                  disabled={userMeta?.roleId === 1 || userMeta?.roleId === 2}
                  searchable
                  showNoResultsText
                />
              </Grid>
            )}
            {roleIdType && [2, 3].includes(roleId) && (
              <Grid size={fullWidthGridStyle}>
                <ArrMultiSelectDropdown
                  dropdownLabel={t('userManagement.addUser.securityGroup')}
                  dropdownItems={securityGRPsOptions.map((item) => ({
                    key: item.key.toString(),
                    value: item.value,
                  }))}
                  selectedValues={securityGroup.map((item) => ({
                    key: item.key.toString(),
                    value: item.value,
                  }))}
                  onSelectionChange={(items: ISelectItem[]) => {
                    const mappedItems: SecurityGroupItem[] = items.map(
                      (item) => ({
                        key: Number(item.key),
                        value: item.value as string,
                        isSelected: true,
                        text: item.value as string,
                      })
                    );
                    setSecurityGroup(mappedItems);
                  }}
                  multiple
                  searchable
                  withControls
                  dataTestId="select-security-grp"
                  checkboxDataTestId="add-user-security-group-dropdown-checkbox"
                  dropdownItemKey={'key'}
                  dropdownItemDisplay={'value'}
                  formControlRestProps={{required: true}}
                  onClearAll={() => {}}
                  onApply={() => {}}
                />
              </Grid>
            )}
          </Grid>
        </form>
      </Box>
      {showToast && !isLoading && (
        <ArrToast
          id="toast-1"
          title={addUserError != null ? t('common.error') : t('common.success')}
          message={
            addUserError != null
              ? addUserError?.message
              : addUserData?.message &&
                t('userManagement.addUser.successTostMSG')
          }
          type={addUserError != null ? 'error' : 'success'}
          duration={TOAST_DURATION}
          onClose={() => setShowToast(false)}
          dismissable={true}
          closeIcon={<CloseToast />}
          position="bottom-right"
        />
      )}
    </Box>
  );

  return (
    <>
      {showDrawer && (
        <ArrDrawer
          showButton={true}
          isOpen={showDrawer}
          size="small"
          side="right"
          onClose={() => handleDrawer(false)}
        >
          {contentBody}
        </ArrDrawer>
      )}
    </>
  );
};
